import React from "react";
import { useTranslation } from "react-i18next";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import PageHero from "@/components/site/PageHero";
import BottomCTA from "@/components/site/BottomCTA";
import useReveal from "@/hooks/useReveal";
import { APPROACH_CARDS } from "@/data/investments";

export default function InvestmentApproach() {
  useReveal();
  const { t } = useTranslation();
  const pillars = t("investmentApproach.pillars", { returnObjects: true });
  const structures = t("investmentApproach.structures", { returnObjects: true });
  // Use existing card imagery from the data file; text comes from i18n
  const pillarImgs = APPROACH_CARDS.map((c) => c.img);

  return (
    <div className="min-h-screen bg-white text-unik-ink">
      <SiteNav />

      <PageHero
        eyebrow={t("investmentApproach.heroEyebrow")}
        title={t("investmentApproach.heroTitle")}
        accent={<><br/>{t("investmentApproach.heroAccent")}</>}
        sub={t("investmentApproach.heroSub")}
        bgImage="/assets/unsplash-1509030450996.jpg"
      />

      {/* Philosophy */}
      <section className="relative bg-white pt-28 lg:pt-40 pb-28 lg:pb-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-20">
            <div className="lg:col-span-5" data-reveal>
              <div className="flex items-center gap-4">
                <span className="block w-12 h-px bg-unik-gold" />
                <div className="eyebrow text-[#14355a]">{t("investmentApproach.philosophyEyebrow")}</div>
              </div>
              <h2
                style={{
                  fontSize: "clamp(2.5rem, 5.2vw, 5rem)",
                  fontWeight: 500,
                  lineHeight: "1.0",
                  letterSpacing: "-0.015em",
                }}
                className="mt-10 font-display text-[#14355a]"
              >
                {t("investmentApproach.philosophyTitle1")}<br/>
                <span className="text-unik-gold">{t("investmentApproach.philosophyTitleGold")}</span><br/>
                {t("investmentApproach.philosophyTitle3")}
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-4" data-reveal style={{ "--reveal-delay": "150ms" }}>
              <p
                className="font-display text-[#14355a] tracking-tight"
                style={{ fontSize: "clamp(1.25rem, 1.65vw, 1.5rem)", fontWeight: 500, lineHeight: 1.45 }}
              >
                {t("investmentApproach.philosophyLead")}
              </p>
              <div className="mt-10 text-[16px] lg:text-[17px] leading-[1.9] text-black max-w-2xl">
                <p>{t("investmentApproach.philosophyBody")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Build platforms */}
      <section className="relative bg-[#f4f5f7] py-28 lg:py-36">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-20">
            <div className="lg:col-span-7" data-reveal>
              <div className="eyebrow text-[#14355a]">{t("investmentApproach.buildEyebrow")}</div>
              <h2
                style={{
                  fontSize: "clamp(2.25rem, 4.5vw, 4rem)",
                  fontWeight: 500,
                  lineHeight: "1.05",
                  letterSpacing: "-0.015em",
                }}
                className="mt-7 font-display text-[#14355a]"
              >
                {t("investmentApproach.buildTitle1")}<br/>
                <span className="text-unik-gold">{t("investmentApproach.buildTitleGold")}</span>
              </h2>
            </div>
            <div className="lg:col-span-5 lg:pt-4" data-reveal style={{ "--reveal-delay": "150ms" }}>
              <p className="text-[17px] lg:text-[18px] leading-[1.9] text-black">
                {t("investmentApproach.buildBody1")}
              </p>
              <p className="mt-6 text-[17px] lg:text-[18px] leading-[1.9] text-black">
                {t("investmentApproach.buildBody2")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars cards */}
      <section className="relative bg-white pt-28 lg:pt-36 pb-0">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div data-reveal className="max-w-3xl mb-16 lg:mb-20">
            <div className="eyebrow text-[#14355a]">{t("investmentApproach.pillarsEyebrow")}</div>
            <h2
              style={{
                fontSize: "clamp(2.25rem, 4.5vw, 4rem)",
                fontWeight: 500,
                lineHeight: "1.05",
              }}
              className="mt-7 font-display text-[#14355a] tracking-tight"
            >
              {t("investmentApproach.pillarsTitle")}
            </h2>
          </div>
        </div>

        <div className="w-full">
          {pillars.map((c, i) => {
            const reverse = i % 2 === 1;
            return (
              <div
                key={c.n}
                data-reveal-fade
                style={{ "--reveal-delay": `${i * 80}ms` }}
                className="grid lg:grid-cols-2 w-full"
              >
                <div className={`${reverse ? "lg:order-2" : ""}`}>
                  <div className="relative w-full aspect-[16/10] overflow-hidden">
                    <img src={pillarImgs[i]} alt={c.t} className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                </div>
                <div className={`flex items-center bg-white ${reverse ? "lg:order-1" : ""}`}>
                  <div className="w-full px-8 sm:px-12 lg:px-20 py-14 lg:py-0 max-w-2xl mx-auto lg:mx-0">
                    <div className="font-display text-unik-gold tracking-[0.2em]" style={{ fontSize: "clamp(2.5rem, 4vw, 4rem)", lineHeight: 1, fontWeight: 400 }}>
                      {c.n}
                    </div>
                    <div className="mt-5 w-12 h-px bg-[#14355a]/30" />
                    <h3 style={{ fontSize: "clamp(1.75rem, 2.6vw, 2.5rem)", fontWeight: 500, lineHeight: 1.1 }} className="mt-7 font-display text-[#14355a] tracking-tight">
                      {c.t}
                    </h3>
                    <p className="mt-7 text-[17px] lg:text-[18px] leading-[1.9] text-black max-w-md">{c.b}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Structures */}
      <section className="relative text-white overflow-hidden">
        <div className="absolute inset-0 silk-bg" />
        <div className="absolute inset-0 grain pointer-events-none opacity-[0.04]" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-10 py-28 lg:py-36">
          <div data-reveal className="max-w-3xl">
            <div className="eyebrow text-unik-gold">{t("investmentApproach.structureEyebrow")}</div>
            <h2
              style={{
                fontSize: "clamp(2.25rem, 4.5vw, 4rem)",
                fontWeight: 500,
                lineHeight: "1.05",
              }}
              className="mt-7 font-display tracking-tight"
            >
              {t("investmentApproach.structureTitle1")}<br/>
              <span className="text-unik-gold">{t("investmentApproach.structureTitle2")}</span>
            </h2>
            <p className="mt-8 text-[17px] lg:text-[18px] leading-[1.9] text-white/85 max-w-2xl">
              {t("investmentApproach.structureBody")}
            </p>
          </div>

          <div className="mt-16 lg:mt-20 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {structures.map((s, i) => (
              <div
                key={s.n}
                data-reveal
                style={{ "--reveal-delay": `${i * 120}ms` }}
                className="bg-[#0e2540] p-10 lg:p-12 hover:bg-[#14355a] transition-colors duration-500"
                data-testid={`capital-structure-${s.n}`}
              >
                <div
                  className="font-display text-unik-gold tracking-[0.15em]"
                  style={{ fontSize: "clamp(2.25rem, 2.8vw, 3rem)", lineHeight: 1, fontWeight: 500 }}
                >
                  {s.n}
                </div>
                <div className="mt-6 w-10 h-px bg-unik-gold/50" />
                <h3
                  style={{ fontSize: "clamp(1.375rem, 1.7vw, 1.625rem)", fontWeight: 500, lineHeight: 1.2 }}
                  className="mt-7 font-display tracking-tight"
                >
                  {s.t}
                </h3>
                <p className="mt-5 text-[15px] lg:text-[16px] leading-[1.85] text-white/80">
                  {s.b}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BottomCTA
        eyebrow={t("investmentApproach.ctaEyebrow")}
        title={t("investmentApproach.ctaTitle")}
        accent={t("investmentApproach.ctaAccent")}
        body={t("investmentApproach.ctaBody")}
        primary={{ label: t("investmentApproach.ctaPrimary"), to: "/investments/portfolio-assets" }}
        secondary={{ label: t("investmentApproach.ctaSecondary"), to: "/contact" }}
      />

      <SiteFooter />
    </div>
  );
}
